import { Modal, TextInput, NumberInput, Group, Button, Stack, Switch, ScrollArea } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createShow, updateShow } from '../../../../redux/reducers/showSlice';
import { Show } from '../../../../models/show.model';
import { selectIsAdmin } from '../../../../redux/selectors/authSelector';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@mantine/hooks';

interface AddEditShowProps {
    opened: boolean;
    onClose: () => void;
    activeShow: Show | null;
}

function AddEditShow({ opened, onClose, activeShow }: AddEditShowProps) {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width: 50em)');
    const isAdmin = useSelector(selectIsAdmin);

    const emptyShow: Partial<Show> = {
        theater_id: '',
        show_name: '',
        show_date: new Date(),
        show_time: '20:00',
        show_duration: 120,
        show_status: 'Scheduled',
        max_seats: 0,
        available_total_seats: 0,
        available_adult_seats: 0,
        available_children_seats: 0,
        is_active: true,
        is_full: false,
        is_almost_full: false
    };

    const [show, setShow] = useState<Partial<Show>>(emptyShow);

    const handleSubmit = () => {
        if (activeShow) {
            dispatch(updateShow({
                id: activeShow._id as string,
                data: show
            } as any));
        } else {
            dispatch(createShow({
                ...show,
                available_total_seats: !show.available_total_seats ? show.max_seats : show.available_total_seats
            } as any));
        }
        onClose();
        setShow(emptyShow);
    };

    useEffect(() => {
        if (activeShow) {
            setShow({
                theater_id: activeShow.theater_id,
                show_name: activeShow.show_name,
                show_date: new Date(activeShow.show_date),
                show_time: activeShow.show_time,
                show_duration: activeShow.show_duration,
                show_status: activeShow.show_status,
                max_seats: activeShow.max_seats,
                available_total_seats: activeShow.available_total_seats,
                available_adult_seats: activeShow.available_adult_seats,
                available_children_seats: activeShow.available_children_seats,
                is_active: activeShow.is_active,
                is_full: activeShow.is_full,
                is_almost_full: activeShow.is_almost_full
            });
        } else {
            setShow(emptyShow);
        }
    }, [activeShow]);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={activeShow ? 'Edit Show' : 'Add New Show'}
            size="lg"
            radius="md"
            fullScreen={isMobile}
            transitionProps={{ transition: 'fade', duration: 200 }}
            scrollAreaComponent={ScrollArea.Autosize}
            removeScrollProps={{ allowPinchZoom: true }}
        >
            <Stack gap="md">
                <TextInput
                    label="Show Name"
                    placeholder="Enter show name"
                    value={show.show_name}
                    onChange={(e) => setShow({ ...show, show_name: e.target.value })}
                    required
                    disabled={!isAdmin}
                />
                <DateInput
                    label="Show Date"
                    placeholder="Select date"
                    value={show.show_date}
                    onChange={(value) => setShow({ ...show, show_date: value as Date })}
                    required
                    readOnly={!!activeShow || !isAdmin}
                />
                <TextInput
                    label="Show Time"
                    placeholder="Enter show time"
                    value={show.show_time}
                    onChange={(e) => setShow({ ...show, show_time: e.target.value })}
                    required
                    disabled={!isAdmin}
                />
                <NumberInput
                    label="Maximum Seats"
                    placeholder="Enter maximum seats"
                    value={show.max_seats}
                    onChange={(value) => {
                        const newMaxSeats = Number(value);
                        const currentBooked = (show.max_seats || 0) - (show.available_total_seats || 0);
                        const newAvailableSeats = Math.max(0, newMaxSeats - currentBooked);
                        setShow({
                            ...show,
                            max_seats: newMaxSeats,
                            available_total_seats: newAvailableSeats
                        });
                    }}
                    min={0}
                    required
                />
                <NumberInput
                    label="Available Total Seats"
                    placeholder="Enter available total seats"
                    value={show.available_total_seats}
                    onChange={(value) => setShow({ ...show, available_total_seats: Number(value) })}
                    min={0}
                    required
                />
                <NumberInput
                    label="Available Adult Seats"
                    placeholder="Enter available adult seats"
                    value={show.available_adult_seats}
                    onChange={(value) => setShow({ ...show, available_adult_seats: Number(value) })}
                    min={0}
                    required
                />
                <NumberInput
                    label="Available Children Seats"
                    placeholder="Enter available children seats"
                    value={show.available_children_seats}
                    onChange={(value) => setShow({ ...show, available_children_seats: Number(value) })}
                    min={0}
                    required
                />
                <NumberInput
                    label="Duration (minutes)"
                    placeholder="Enter duration in minutes"
                    value={show.show_duration}
                    onChange={(value) => setShow({ ...show, show_duration: Number(value) })}
                    min={0}
                    required
                    disabled={!isAdmin}
                />
                <Switch
                    label="Full"
                    checked={show.is_full}
                    onChange={(e) => setShow({ ...show, is_full: e.currentTarget.checked })}
                />
                <Switch
                    label="Almost Full"
                    checked={show.is_almost_full}
                    onChange={(e) => setShow({ ...show, is_almost_full: e.currentTarget.checked })}
                />
                <Switch
                    label="Active"
                    checked={show.is_active}
                    onChange={(e) => setShow({ ...show, is_active: e.currentTarget.checked })}
                    disabled
                />
            </Stack>
            <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit}>{activeShow ? 'Update Show' : 'Create Show'}</Button>
            </Group>
        </Modal>
    );
}

export default AddEditShow;